import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  AxiosError,
} from "axios";
import { API_CONFIG } from "./env";
import { ENDPOINTS } from "./endpoints";
import {
  getAccessToken,
  clearAuthData,
  getRefreshToken,
  setAccessToken,
  setRefreshToken,
} from "../../utils/storage";
import {
  notifyTokenRefreshFailed,
  notifyTokenRefreshStarting,
  notifyTokenRefreshSuccess,
} from "../../utils/tokenRefreshDebug";

const LEGACY_UPLOAD_SEGMENT = "/public/uploads/all/";
const S3_UPLOAD_SEGMENT = "/old_uploads/all/";
const DEFAULT_MEDIA_S3_BASE_URL =
  "https://nyc3.digitaloceanspaces.com/bizcover-media-uploads";
const MEDIA_S3_BASE_URL = (
  import.meta.env.VITE_MEDIA_S3_BASE_URL ||
  import.meta.env.VITE_DO_SPACES_PUBLIC_BASE_URL ||
  DEFAULT_MEDIA_S3_BASE_URL
).replace(/\/+$/, "");

function rewriteLegacyUploadUrl(value: string): string {
  const slashSafeBase = MEDIA_S3_BASE_URL.endsWith("/")
    ? MEDIA_S3_BASE_URL.slice(0, -1)
    : MEDIA_S3_BASE_URL;
  const targetPrefix = `${slashSafeBase}${S3_UPLOAD_SEGMENT}`;

  if (value.includes(LEGACY_UPLOAD_SEGMENT)) {
    const suffix = value.split(LEGACY_UPLOAD_SEGMENT)[1];
    if (suffix) return `${targetPrefix}${suffix}`;
  }

  if (value.includes(S3_UPLOAD_SEGMENT)) {
    const suffix = value.split(S3_UPLOAD_SEGMENT)[1];
    if (suffix) return `${targetPrefix}${suffix}`;
  }

  return value;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (typeof value !== "object" || value === null) return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function normalizeLegacyUploadUrls<T>(payload: T, seen = new WeakSet()): T {
  if (typeof payload === "string") {
    return rewriteLegacyUploadUrl(payload) as T;
  }

  if (Array.isArray(payload)) {
    for (let i = 0; i < payload.length; i += 1) {
      payload[i] = normalizeLegacyUploadUrls(payload[i], seen);
    }
    return payload;
  }

  if (!isPlainObject(payload)) {
    return payload;
  }

  if (seen.has(payload)) {
    return payload;
  }

  seen.add(payload);
  const mutablePayload = payload as Record<string, unknown>;
  for (const [key, value] of Object.entries(mutablePayload)) {
    mutablePayload[key] = normalizeLegacyUploadUrls(value, seen);
  }

  return payload;
}

/**
 * Custom error class for API errors
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public status?: number,
    public code?: string,
    public response?: any,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

let refreshInFlight: Promise<boolean> | null = null;
const REFRESH_URL = `${API_CONFIG.BASE_URL.replace(/\/+$/, "")}${ENDPOINTS.AUTH.REFRESH}`;

function shouldRedirectToLoginFromPath(pathname: string): boolean {
  const publicPrefixes = [
    "/",
    "/home",
    "/bizcoverindia-home-updated",
    "/categories",
    "/search",
    "/section",
    "/banner",
    "/product",
    "/sellers",
    "/supplier",
    "/pricing",
    "/membership-plans",
    "/unauthorized",
    "/figma-header-preview",
    "/figma-footer-preview",
    "/login",
    "/registration",
    "/forgot-password",
    "/reset-password",
  ];

  if (publicPrefixes.some((prefix) =>
    prefix === "/" ? pathname === "/" : pathname === prefix || pathname.startsWith(`${prefix}/`)
  )) {
    return false;
  }

  return true;
}

function refreshSessionWithRefreshToken(): Promise<boolean> {
  if (refreshInFlight) {
    return refreshInFlight;
  }
  refreshInFlight = (async () => {
    try {
      const rt = getRefreshToken();
      if (!rt) {
        notifyTokenRefreshFailed("No refresh token in storage.");
        return false;
      }
      const { data: envelope } = await axios.post<{
        success?: boolean;
        data?: { access_token?: string; refresh_token?: string; expires_in?: number };
      }>(
        REFRESH_URL,
        { refresh_token: rt },
        {
          headers: { "Content-Type": "application/json" },
          timeout: API_CONFIG.TIMEOUT,
        },
      );
      const inner = (envelope as { data?: unknown })?.data ?? envelope;
      const body = inner as {
        access_token?: string;
        refresh_token?: string;
      };
      if (!body.access_token || typeof body.access_token !== "string") {
        notifyTokenRefreshFailed("Response missing access_token.");
        return false;
      }
      setAccessToken(body.access_token);
      if (body.refresh_token && typeof body.refresh_token === "string") {
        setRefreshToken(body.refresh_token);
      }
      notifyTokenRefreshSuccess();
      return true;
    } catch (e) {
      notifyTokenRefreshFailed(
        e instanceof Error ? e.message : "Network or API error.",
      );
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

/**
 * Axios instance configuration
 */
const createAxiosInstance = (): AxiosInstance => {
  const instance = axios.create({
    baseURL: API_CONFIG.BASE_URL,
    timeout: API_CONFIG.TIMEOUT,
    headers: {
      "Content-Type": "application/json",
    },
  });

  // Request interceptor for token injection (backend JWT from localStorage)
  instance.interceptors.request.use(
    async (config) => {
      try {
        const token = await getAccessToken();

        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
          if (import.meta.env.DEV) {
            console.log(
              "🔑 Token added to request:",
              config.url,
              token.substring(0, 20) + "...",
            );
          }
        }

        if (!token) {
          const isPublicEndpoint =
            config.url?.includes("/auth/login") ||
            config.url?.includes("/auth/google") ||
            config.url?.includes("/auth/register") ||
            config.url?.includes("/auth/refresh") ||
            config.url?.includes("/auth/send-signup-otp") ||
            config.url?.includes("/auth/verify-signup-otp") ||
            config.url?.includes("/auth/check-shop-name") ||
            config.url?.includes("/health");
          if (!isPublicEndpoint && import.meta.env.DEV) {
            console.warn("⚠️ No token available for request:", config.url);
          }
        }
      } catch (error) {
        console.warn("Failed to get access token:", error);
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    },
  );

  // Response interceptor for error handling
  instance.interceptors.response.use(
    (response: AxiosResponse) => {
      response.data = normalizeLegacyUploadUrls(response.data);
      return response;
    },
    async (error: AxiosError) => {
      const originalRequest = error.config as AxiosRequestConfig & {
        _retry?: boolean;
      };

      const extractMessage = (data: unknown): string | undefined => {
        const raw = (data as { message?: string | string[] })?.message;
        if (raw == null) return undefined;
        return Array.isArray(raw) ? raw.join(" ") : String(raw);
      };

      const url = originalRequest.url ?? "";

      if (error.response?.status === 401) {
        const { status, data } = error.response;
        const serverMessage = extractMessage(data) || "Unauthorized";

        const isAuthCredentialRequest =
          url.includes("/auth/login") ||
          url.includes("/auth/google") ||
          url.includes("/auth/register");

        if (isAuthCredentialRequest) {
          return Promise.reject(
            new ApiError(serverMessage, status, "UNAUTHORIZED"),
          );
        }

        if (url.includes("/auth/refresh")) {
          clearAuthData();
          if (
            typeof window !== "undefined" &&
            shouldRedirectToLoginFromPath(window.location.pathname)
          ) {
            window.location.href = "/login";
          }
          return Promise.reject(
            new ApiError(serverMessage, status, "UNAUTHORIZED"),
          );
        }

        if (!originalRequest._retry) {
          originalRequest._retry = true;
          notifyTokenRefreshStarting(
            typeof originalRequest.url === "string" ? originalRequest.url : undefined,
          );
          const refreshed = await refreshSessionWithRefreshToken();
          if (refreshed) {
            const newToken = await getAccessToken();
            if (newToken) {
              originalRequest.headers = originalRequest.headers ?? {};
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
            }
            return instance(originalRequest as AxiosRequestConfig);
          }
        }

        clearAuthData();

        if (
          typeof window !== "undefined" &&
          shouldRedirectToLoginFromPath(window.location.pathname)
        ) {
          console.log("🔄 Redirecting to /login due to 401 error");
          window.location.href = "/login";
        }

        return Promise.reject(
          new ApiError(serverMessage, status, "UNAUTHORIZED"),
        );
      }

      if (error.response) {
        const { status, data } = error.response;
        const raw = (data as any)?.message;
        const message = Array.isArray(raw)
          ? raw.join(" ")
          : raw || error.message || "An error occurred";

        return Promise.reject(
          new ApiError(message, status, (data as any)?.code),
        );
      }

      if (error.request) {
        if (error.code === "ECONNABORTED") {
          return Promise.reject(
            new ApiError(
              "Upload request timed out. Please retry, or use a smaller/lower-resolution file.",
              undefined,
              "REQUEST_TIMEOUT",
            ),
          );
        }

        if (typeof navigator !== "undefined" && navigator.onLine === false) {
          return Promise.reject(
            new ApiError("No internet connection. Please check your network and retry.", undefined, "OFFLINE"),
          );
        }

        return Promise.reject(
          new ApiError(
            error.message || "Network error while contacting server",
            undefined,
            "NETWORK_ERROR",
          ),
        );
      }

      return Promise.reject(
        new ApiError(error.message || "An unexpected error occurred"),
      );
    },
  );

  return instance;
};

// Create and export the axios instance
export const axiosClient = createAxiosInstance();

/**
 * Generic API response wrapper
 */
export interface ApiResponse<T = any> {
  data: T;
  message?: string;
  success: boolean;
  status: number;
}

/**
 * Paginated response interface
 */
export interface PaginatedResponse<T = any> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
  message?: string;
  success: boolean;
  status: number;
}

/**
 * Query parameters interface
 */
export interface QueryParams {
  page?: number;
  limit?: number;
  search?: string;
  sort?: string;
  order?: "asc" | "desc";
  [key: string]: any;
}

/**
 * File upload configuration
 */
export interface UploadConfig {
  onUploadProgress?: (progressEvent: any) => void;
  timeout?: number;
}

export default axiosClient;
